import * as p from '@clack/prompts'
import { execFileSync } from 'node:child_process'
import fs from 'node:fs'
import path from 'node:path'
import type { InitCommandOptions } from '@cli-types/core-engine/commands.js'
import type { ResolvedBetterstartConfig } from '@cli-types/core-engine/config.js'
import type { InitJsonContext, SeedOverwriteMode } from '@cli-types/next-adapter/commands.js'
import type {
  PresetPromptResult,
  ProjectPromptResult,
  DatabaseProvider,
  DeployProvider,
  StorageProvider
} from '@cli-types/next-adapter/init.js'
import type { RailwaySession } from '@cli-types/next-adapter/railway.js'
import type { CollectedIntegrationConfig } from '@cli-types/next-adapter/runtime.js'
import { DRIZZLE_KIT_POSTGRES_DRIVER_DEP, FRESH_PNPM_ALLOWED_BUILDS } from '@cli-constants/deps.js'
import { CONFIG_FILE_NAME } from '@cli-constants/paths.js'
import {
  DEFAULT_ADMIN_NAMESPACE,
  R2_ENV_KEYS,
  RAILWAY_BUCKET_ENV_KEYS
} from '@cli-constants/scaffolding.js'
import { appendEnvVars, readProjectEnvVar } from '@cli-utils/files.js'
import { detectPackageManager, redactSecrets, runCommand } from '@cli-utils/process.js'
import { maskUrlCredentials } from '@cli-utils/process.js'
import { pmExecCommand, runCapture } from '@cli-utils/process.js'
import {
  clackLogRows,
  clackPromptRows,
  eraseRowsAbove,
  installSetupCancelGuard,
  spinner
} from '@cli-utils/terminal.js'
import { writeConfigFile } from '@core-engine/config/serialize/write-config-file.js'
import { detectNextMajorVersion } from '@next-adapter/config/detect/detect-next-major-version.js'
import { detectProject } from '@next-adapter/config/detect/detect-project.js'
import { getDefaultConfig } from '@next-adapter/config/get-default-config.js'
import { deriveMigrationsDir } from '@next-adapter/config/namespace/derive-migrations-dir.js'
import { deriveNamespacePaths } from '@next-adapter/config/namespace/derive-namespace-paths.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace/resolve-admin-namespace.js'
import { validateAdminDashboardPath } from '@next-adapter/config/namespace/validate-admin-dashboard-path.js'
import { validateAdminNamespace } from '@next-adapter/config/namespace/validate-admin-namespace.js'
import { createNextAppCommand } from '@next-adapter/create-next-app-command.js'
import { runDrizzlePush } from '@next-adapter/drizzle-push/run-drizzle-push.js'
import { openBrowserVercelNeonResource } from '@next-adapter/init/prompts/database/open-browser-vercel-neon-resource.js'
import { openBrowserVercelNeon } from '@next-adapter/init/prompts/database/open-browser-vercel-neon.js'
import { promptConnectionString } from '@next-adapter/init/prompts/database/prompt-connection-string.js'
import { promptServices } from '@next-adapter/init/prompts/database/prompt-services.js'
import { promptPresets } from '@next-adapter/init/prompts/presets/prompt-presets.js'
import { promptProject } from '@next-adapter/init/prompts/project.js'
import { addStorageIntegration } from '@next-adapter/init/providers/add-storage-integration.js'
import { promptDeployProvider } from '@next-adapter/init/providers/prompt-deploy-provider.js'
import { resolveFlagStorageProvider } from '@next-adapter/init/providers/resolve-flag-storage-provider.js'
import { validateInitProviderFlags } from '@next-adapter/init/providers/validate-init-provider-flags.js'
import { validateResolvedDatabaseProvider } from '@next-adapter/init/providers/validate-resolved-database-provider.js'
import { buildRailwayProviderOverrides } from '@next-adapter/init/railway/deploy/build-railway-provider-overrides.js'
import { runRailwayDeployFlow } from '@next-adapter/init/railway/deploy/run-railway-deploy-flow.js'
import { ensureRailwayBucket } from '@next-adapter/init/railway/resources/ensure-railway-bucket.js'
import { ensureRailwayPostgres } from '@next-adapter/init/railway/resources/ensure-railway-postgres.js'
import { createRailwaySession } from '@next-adapter/init/railway/session.js'
import { scaffoldApiRoutes } from '@next-adapter/init/scaffolders/api-routes.js'
import { scaffoldAuth } from '@next-adapter/init/scaffolders/auth.js'
import { scaffoldBase } from '@next-adapter/init/scaffolders/base.js'
import { scaffoldComponents } from '@next-adapter/init/scaffolders/components/scaffold-components.js'
import { scaffoldCoreSchemas } from '@next-adapter/init/scaffolders/core-schemas/scaffold-core-schemas.js'
import { scaffoldDatabase } from '@next-adapter/init/scaffolders/database.js'
import { getCliDependencySyncPlan } from '@next-adapter/init/scaffolders/dependencies/get-cli-dependency-sync-plan.js'
import { getDependencyPlan } from '@next-adapter/init/scaffolders/dependencies/get-dependency-plan.js'
import { hasDrizzleKitPostgresDriverDependency } from '@next-adapter/init/scaffolders/dependencies/has-drizzle-kit-postgres-driver-dependency.js'
import { installDependenciesAsync } from '@next-adapter/init/scaffolders/dependencies/install-dependencies-async.js'
import { detectDevPort } from '@next-adapter/init/scaffolders/dev-server/detect-dev-port.js'
import { ensureDefaultDevScriptPort } from '@next-adapter/init/scaffolders/dev-server/ensure-default-dev-script-port.js'
import { persistBlobReadWriteToken } from '@next-adapter/init/scaffolders/env/persist-blob-read-write-token.js'
import { persistDatabaseUrl } from '@next-adapter/init/scaffolders/env/persist-database-url.js'
import { scaffoldEnv } from '@next-adapter/init/scaffolders/env/scaffold-env.js'
import { scaffoldLayout } from '@next-adapter/init/scaffolders/layout.js'
import { scaffoldNextConfig } from '@next-adapter/init/scaffolders/next-config/scaffold-next-config.js'
import { scaffoldOxfmt } from '@next-adapter/init/scaffolders/oxfmt/scaffold-oxfmt.js'
import { scaffoldTailwind } from '@next-adapter/init/scaffolders/tailwind/scaffold-tailwind.js'
import { scaffoldTsconfig } from '@next-adapter/init/scaffolders/tsconfig/scaffold-tsconfig.js'
import { runVercelBlobFlow } from '@next-adapter/init/vercel/flow/run-vercel-blob-flow.js'
import { runVercelDeployFlow } from '@next-adapter/init/vercel/flow/run-vercel-deploy-flow.js'
import { runVercelNeonFlow } from '@next-adapter/init/vercel/flow/run-vercel-neon-flow.js'
import { readLinkedProjectId } from '@next-adapter/init/vercel/project/read-linked-project-id.js'
import { isEmailProviderIntegration } from '@next-adapter/integration-registry.js'
import { collectIntegrationConfig } from '@next-adapter/integration-runtime/collect-integration-config.js'
import { installIntegrations } from '@next-adapter/integration-runtime/install-integrations.js'
import { parseIntegrationList } from '@next-adapter/integration-runtime/parse-integration-list.js'
import { installPresets } from '@next-adapter/preset-runtime/install-presets.js'
import { parsePresetList } from '@next-adapter/preset-runtime/parse-preset-list.js'
import pc from 'picocolors'
import { checkExistingAdmin } from './check-existing-admin.js'
import { formatAdminIdentity } from './format-admin-identity.js'
import { hasDbUrl } from './has-db-url.js'
import { hasRailwayBucketConfig } from './has-railway-bucket-config.js'
import { isDatabaseReachabilityError } from './is-database-reachability-error.js'
import { isValidDbUrl } from './is-valid-db-url.js'
import { railwayBucketIntegrationConfig } from './railway-bucket-integration-config.js'
import { readExistingDbUrl } from './read-existing-db-url.js'
import { removeExistingAdminPaths } from './remove-existing-admin-paths.js'
import { renderInitBanner } from './render-init-banner.js'
import { resolveForceInitNamespaces } from './resolve-force-init-namespaces.js'
import { resolveNonInteractiveProject } from './resolve-non-interactive-project.js'
import { runSeed } from './run-seed.js'
import { shouldUseTerminalForDbPush } from './should-use-terminal-for-db-push.js'
import { startManagedDevServer } from './start-managed-dev-server.js'
import { verifyDatabaseReachable } from './verify-database-reachable.js'
import { writeInitJson } from './write-init-json.js'

export async function runInitCommandInternal(
  name: string | undefined,
  options: InitCommandOptions,
  jsonContext: InitJsonContext
): Promise<void> {
  const exitInit = (phase: string, message: string, code = 'INIT_FAILED'): never => {
    if (options.json) {
      writeInitJson(jsonContext, {
        success: false,
        phase,
        error: {
          code,
          message: redactSecrets(message)
        }
      })
    }
    process.exit(1)
  }

  if (options.json) {
    if (!options.yes) {
      const message = '--json requires --yes.'
      p.log.error(message)
      exitInit('validation', message, 'JSON_REQUIRES_YES')
    }
  }

  const disposeCancelGuard = installSetupCancelGuard()

  renderInitBanner()

  let selectedPresets: ReturnType<typeof parsePresetList> = []
  let selectedIntegrations: ReturnType<typeof parseIntegrationList> = []
  let flagStorageProvider: StorageProvider | undefined
  try {
    validateInitProviderFlags(options)
    selectedPresets = options.presets ? parsePresetList(options.presets) : []
    selectedIntegrations = options.integrations ? parseIntegrationList(options.integrations) : []
    flagStorageProvider = resolveFlagStorageProvider(options.storageProvider, selectedIntegrations)
    if (flagStorageProvider) {
      selectedIntegrations = addStorageIntegration(selectedIntegrations, flagStorageProvider)
    }
  } catch (error) {
    disposeCancelGuard()
    const message = error instanceof Error ? error.message : String(error)
    p.log.error(message)
    exitInit('validation', message, 'INVALID_OPTIONS')
  }

  let cwd = process.cwd()
  let projectName = path.basename(cwd)
  let forceMode = Boolean(options.force)
  let namespace = DEFAULT_ADMIN_NAMESPACE
  if (options.namespace) {
    try {
      namespace = validateAdminNamespace(options.namespace)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      p.log.error(message)
      exitInit('validation', message, 'INVALID_NAMESPACE')
    }
  }

  let projectPrompt: ProjectPromptResult | undefined
  if (!detectProject(cwd, namespace).isExisting) {
    try {
      projectPrompt = options.yes ? resolveNonInteractiveProject(name) : await promptProject(name)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      p.log.error(message)
      exitInit('project', message, 'PROJECT_SETUP_FAILED')
    }
  }

  if (!options.yes && !options.namespace) {
    const defaultDashboardPath = resolveAdminNamespace(DEFAULT_ADMIN_NAMESPACE).routePath
    const namespaceInput = await p.text({
      message: 'Enter the dashboard path',
      placeholder: `eg. ${defaultDashboardPath}`,
      defaultValue: defaultDashboardPath,
      validate(value) {
        try {
          validateAdminDashboardPath(value ?? defaultDashboardPath)
          return
        } catch (error) {
          return error instanceof Error ? error.message : String(error)
        }
      }
    })
    if (p.isCancel(namespaceInput)) {
      p.cancel('Setup cancelled.')
      process.exit(0)
    }
    namespace = validateAdminDashboardPath(namespaceInput)
  }

  let project = detectProject(cwd, namespace)
  let pm = detectPackageManager(cwd)
  let isFreshProject = false

  let srcDir: boolean
  if (project.isExisting) {
    srcDir = project.hasSrcDir

    if (!project.hasTypeScript) {
      const message = 'TypeScript is required. Please add a tsconfig.json first.'
      p.log.error(message)
      exitInit('project', message, 'TYPESCRIPT_REQUIRED')
    }

    if (forceMode) {
      const nuked = removeExistingAdminPaths(cwd, await resolveForceInitNamespaces(cwd, namespace))
      if (nuked > 0) {
        p.log.warn(`${pc.yellow('Force mode:')} removed ${nuked} existing admin paths`)
      }
      project = detectProject(cwd, namespace)
    } else if (project.conflicts.length > 0) {
      const conflictLines = project.conflicts.map((c) => `${pc.yellow('▲')} ${c}`)
      conflictLines.push(
        '',
        pc.dim(`Use ${pc.bold('--force')} to remove existing admin files before scaffolding.`)
      )
      p.note(conflictLines.join('\n'), pc.yellow('Conflicts'))
      if (options.yes) {
        const message =
          "Can't continue with --yes while admin files conflict. Re-run with --force to remove them first."
        p.log.error(message)
        exitInit('project', message, 'ADMIN_FILES_CONFLICT')
      }

      const proceed = await p.confirm({
        message: [
          `Continue with ${pc.bold(pc.cyan('--force'))}?`,
          `${pc.cyan('│')}  ${pc.dim('This will force overwrite the existing admin code.')}`,
          pc.cyan('│')
        ].join('\n'),
        initialValue: true
      })
      if (p.isCancel(proceed) || !proceed) {
        p.cancel('Setup cancelled.')
        process.exit(0)
      }

      forceMode = true
      const nuked = removeExistingAdminPaths(cwd, await resolveForceInitNamespaces(cwd, namespace))
      if (nuked > 0) {
        p.log.warn(`${pc.yellow('Force mode:')} removed ${nuked} existing admin paths`)
      }
      project = detectProject(cwd, namespace)
    }
  } else {
    const freshProject = projectPrompt as ProjectPromptResult
    srcDir = false

    if (!options.yes) {
      const pmChoice = await p.select({
        message: 'Which package manager do you want to use?',
        options: [
          { value: 'pnpm' as const, label: 'pnpm', hint: 'recommended' },
          { value: 'npm' as const, label: 'npm' },
          { value: 'bun' as const, label: 'bun' }
        ]
      })
      if (p.isCancel(pmChoice)) {
        p.cancel('Setup cancelled.')
        process.exit(0)
      }
      pm = pmChoice
    }

    // Bootstrap the exact create-next-app package through npm without installing yet.
    // BetterStart installs once after the generated package.json has a supported pnpm pin.
    const displayName =
      freshProject.projectName === '.' ? path.basename(cwd) : freshProject.projectName
    projectName = displayName

    const { bin, prefix } = createNextAppCommand()
    const cnaArgs = [
      ...prefix,
      freshProject.projectName,
      '--yes',
      '--typescript',
      '--tailwind',
      '--app',
      '--turbopack',
      '--no-linter',
      '--react-compiler',
      `--use-${pm}`,
      '--skip-install'
    ]
    if (srcDir) cnaArgs.push('--src-dir')
    else cnaArgs.push('--no-src-dir')

    const createNextAppSpinner = spinner()
    createNextAppSpinner.start(`Creating a Next.js app (latest)`)

    const createNextAppResult = await runCapture(bin, cnaArgs, {
      cwd,
      timeoutMs: 120_000
    })

    if (!createNextAppResult.success) {
      createNextAppSpinner.stop(`Failed to create Next.js app: ${displayName}`)
      if (createNextAppResult.output.trim().length > 0) {
        process.stderr.write(`${createNextAppResult.output.trimEnd()}\n`)
      }
      p.log.error(createNextAppResult.error)
      p.log.info(
        `You can create the project manually:\n  ${pc.cyan(`npx create-next-app@latest ${freshProject.projectName} --typescript --tailwind --app`)}\n  Then run ${pc.cyan('betterstart init')} inside it.`
      )
      exitInit('project', createNextAppResult.error, 'CREATE_NEXT_APP_FAILED')
    }

    cwd = path.resolve(cwd, freshProject.projectName)

    const hasPackageJson = fs.existsSync(path.join(cwd, 'package.json'))
    const hasNextConfig = ['next.config.ts', 'next.config.js', 'next.config.mjs'].some((f) =>
      fs.existsSync(path.join(cwd, f))
    )

    if (!hasPackageJson || !hasNextConfig) {
      createNextAppSpinner.stop(`Failed to create Next.js app: ${displayName}`)
      p.log.error(
        'create-next-app completed but the project was not created. This can happen with nested npx calls.'
      )
      const manualCmd = `npx create-next-app@latest ${freshProject.projectName} --typescript --tailwind --app`
      p.log.info(
        `Create the project manually:\n  ${pc.cyan(manualCmd)}\n  Then run ${pc.cyan('betterstart init')} inside it.`
      )
      exitInit(
        'project',
        'create-next-app completed but the project was not created.',
        'CREATE_NEXT_APP_INCOMPLETE'
      )
    }

    createNextAppSpinner.clear()
    project = detectProject(cwd, namespace)
    isFreshProject = true
  }

  let databaseUrl: string | undefined
  let dismissVercelSignedInNote: (() => void) | undefined
  let railwaySessionPromise: Promise<RailwaySession> | undefined
  let railwayDatabaseServiceName: string | undefined
  let railwayBucketResourceName: string | undefined
  let railwayBucketUrlStyle: string | undefined
  const existingDatabase = readExistingDbUrl(cwd)
  const existingDbUrl = existingDatabase?.value
  const getRailwaySession = (): Promise<RailwaySession> => {
    railwaySessionPromise ??= createRailwaySession({
      cwd,
      projectName,
      interactive: !options.yes,
      workspace: options.railwayWorkspace,
      project: options.railwayProject,
      environment: options.railwayEnvironment,
      env: process.env
    }).catch((error) => {
      railwaySessionPromise = undefined
      throw error
    })
    return railwaySessionPromise
  }

  let promptedManualUrl: string | undefined
  let databaseProvider: DatabaseProvider
  if (options.databaseUrl) {
    databaseProvider = 'manual'
  } else if (options.databaseProvider) {
    databaseProvider = options.databaseProvider
  } else if (existingDbUrl || options.yes) {
    databaseProvider = 'manual'
  } else {
    const servicesResult = await promptServices()
    databaseProvider = servicesResult.provider
    promptedManualUrl = servicesResult.url
  }

  try {
    validateResolvedDatabaseProvider(databaseProvider, options)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    p.log.error(message)
    exitInit('providers', message, 'INVALID_DATABASE_PROVIDER')
  }

  if (databaseProvider === 'manual') {
    const candidate = options.databaseUrl ?? existingDbUrl ?? promptedManualUrl
    if (candidate && !isValidDbUrl(candidate)) {
      const message = 'Invalid database URL. Must start with postgres:// or postgresql://'
      p.log.error(message)
      exitInit('providers', message, 'INVALID_DATABASE_URL')
    }
    if (candidate) {
      databaseUrl = candidate
      if (existingDbUrl === candidate && !options.databaseUrl) {
        p.log.info(
          `Using the existing DATABASE_URL from ${existingDatabase?.source ?? 'project env'} ${pc.dim(`(${maskUrlCredentials(candidate, 'postgres://***')})`)}`
        )
      }
    } else if (options.useExistingResources) {
      const message = 'DATABASE_URL is missing from the project environment files.'
      p.log.error(message)
      exitInit('providers', message, 'MISSING_DATABASE_URL')
    } else if (!options.yes) {
      databaseUrl = await promptConnectionString()
    }
  } else if (databaseProvider === 'vercel') {
    const flow = await runVercelNeonFlow({
      cwd,
      projectName,
      interactive: !options.yes,
      plan: options.databasePlan,
      env: process.env
    })
    if (flow.ok && flow.databaseUrl) {
      databaseUrl = flow.databaseUrl
      persistDatabaseUrl(cwd, databaseUrl)
      dismissVercelSignedInNote = flow.dismissSignedInNote
    } else if (options.yes) {
      const message = flow.ok
        ? 'Created a Neon database, but DATABASE_URL could not be retrieved from Vercel.'
        : 'Vercel database provisioning did not complete.'
      p.log.error(message)
      exitInit('providers', message, 'DATABASE_PROVISIONING_FAILED')
    } else if (flow.ok) {
      openBrowserVercelNeonResource(flow.dashboardUrl ?? flow.resourceUrl)
      databaseUrl = await promptConnectionString()
      persistDatabaseUrl(cwd, databaseUrl)
    } else {
      p.log.info('Falling back to a manual database connection string.')
      openBrowserVercelNeon()
      databaseUrl = await promptConnectionString()
    }
  } else {
    try {
      const session = await getRailwaySession()
      const postgres = await ensureRailwayPostgres(session, cwd)
      databaseUrl = postgres.databaseUrl
      railwayDatabaseServiceName = postgres.service.name
      persistDatabaseUrl(cwd, databaseUrl)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      if (options.yes) {
        const failureMessage = `Railway database provisioning failed: ${message}`
        p.log.error(failureMessage)
        exitInit('providers', failureMessage, 'DATABASE_PROVISIONING_FAILED')
      }
      p.log.warn(`Railway database provisioning failed: ${message}`)
      p.log.info('Falling back to a manual database connection string.')
      databaseUrl = await promptConnectionString()
    }
  }

  let collectedIntegrationConfig: CollectedIntegrationConfig = {
    sections: [],
    overwriteKeys: new Set<string>()
  }
  let presetSelection: Omit<PresetPromptResult, 'integrationConfig'>

  const mergeCollectedIntegrationConfig = (next: CollectedIntegrationConfig): void => {
    collectedIntegrationConfig.sections.push(...next.sections)
    for (const key of next.overwriteKeys) {
      collectedIntegrationConfig.overwriteKeys.add(key)
    }
  }

  const provisionRailwayBucket = async (): Promise<{
    ok: boolean
    config?: CollectedIntegrationConfig
  }> => {
    try {
      const region = options.railwayBucketRegion ?? 'iad'
      const session = await getRailwaySession()
      const bucket = await ensureRailwayBucket(session, cwd, {
        name: 'media',
        region
      })
      const config = railwayBucketIntegrationConfig(bucket.credentials)
      appendEnvVars(cwd, config.sections, config.overwriteKeys)
      railwayBucketResourceName = bucket.resource.name
      railwayBucketUrlStyle = bucket.credentials.urlStyle
      return { ok: true, config }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      p.log.warn(`Railway bucket provisioning failed: ${message}`)
      return { ok: false }
    }
  }

  if (selectedPresets.length > 0 || selectedIntegrations.length > 0 || flagStorageProvider) {
    const storage = flagStorageProvider ?? 'local'
    presetSelection = {
      presets: selectedPresets,
      integrations: selectedIntegrations,
      storage
    }

    if (storage === 'r2') {
      const missingR2Keys = R2_ENV_KEYS.filter((key) => !readProjectEnvVar(cwd, key)?.trim())
      if (options.yes && missingR2Keys.length > 0) {
        const message = `Cloudflare R2 is missing required environment variables: ${missingR2Keys.join(', ')}.`
        p.log.error(message)
        exitInit('providers', message, 'MISSING_STORAGE_CREDENTIALS')
      }
      mergeCollectedIntegrationConfig(await collectIntegrationConfig(cwd, ['r2']))
    } else if (storage === 'railway-bucket') {
      if (hasRailwayBucketConfig(cwd)) {
        mergeCollectedIntegrationConfig(await collectIntegrationConfig(cwd, ['railway-bucket']))
      } else if (options.useExistingResources) {
        const missingKeys = RAILWAY_BUCKET_ENV_KEYS.filter(
          (key) => !readProjectEnvVar(cwd, key)?.trim()
        )
        const message = `Railway Bucket is missing required environment variables: ${missingKeys.join(', ')}.`
        p.log.error(message)
        exitInit('providers', message, 'MISSING_STORAGE_CREDENTIALS')
      } else {
        const flow = await provisionRailwayBucket()
        if (flow.ok && flow.config) {
          mergeCollectedIntegrationConfig(flow.config)
        } else if (options.yes) {
          const message = 'Railway bucket provisioning did not complete.'
          p.log.error(message)
          exitInit('providers', message, 'STORAGE_PROVISIONING_FAILED')
        }
      }
    } else if (
      storage === 'vercel-blob' &&
      !readProjectEnvVar(cwd, 'BLOB_READ_WRITE_TOKEN')?.trim()
    ) {
      if (options.useExistingResources) {
        const message = 'BLOB_READ_WRITE_TOKEN is missing from the project environment files.'
        p.log.error(message)
        exitInit('providers', message, 'MISSING_STORAGE_CREDENTIALS')
      }
      const flow = await runVercelBlobFlow({
        cwd,
        projectName,
        interactive: !options.yes,
        env: process.env
      })
      if (flow.ok && flow.token) {
        persistBlobReadWriteToken(cwd, flow.token)
        collectedIntegrationConfig.sections.push({
          header: 'Storage (Vercel Blob)',
          vars: [{ key: 'BLOB_READ_WRITE_TOKEN', value: flow.token }]
        })
        collectedIntegrationConfig.overwriteKeys.add('BLOB_READ_WRITE_TOKEN')
      } else if (options.yes) {
        const message = 'Vercel Blob provisioning did not complete.'
        p.log.error(message)
        exitInit('providers', message, 'STORAGE_PROVISIONING_FAILED')
      } else {
        p.log.warn(
          'Continuing without a Blob token — add BLOB_READ_WRITE_TOKEN to .env.local from the store on vercel.com/dashboard/stores.'
        )
        collectedIntegrationConfig.sections.push({
          header: 'Storage (Vercel Blob)',
          vars: [{ key: 'BLOB_READ_WRITE_TOKEN', value: '' }]
        })
      }
    } else if (storage === 'vercel-blob') {
      mergeCollectedIntegrationConfig(await collectIntegrationConfig(cwd, ['vercel-blob']))
    }
  } else if (options.yes) {
    presetSelection = { presets: [], integrations: [], storage: 'local' }
  } else {
    dismissVercelSignedInNote?.()
    const promptResult = await promptPresets(cwd, {
      provisionVercelBlob: () =>
        runVercelBlobFlow({ cwd, projectName, interactive: true, env: process.env }),
      provisionRailwayBucket
    })
    collectedIntegrationConfig = promptResult.integrationConfig
    presetSelection = {
      presets: promptResult.presets,
      integrations: promptResult.integrations,
      storage: promptResult.storage
    }
  }

  const nextMajorVersion = detectNextMajorVersion(cwd)
  const config: ResolvedBetterstartConfig = {
    ...getDefaultConfig(srcDir, nextMajorVersion)
  }
  config.frameworkConfig.next.namespace = namespace
  config.frameworkConfig.next.paths = deriveNamespacePaths(namespace, srcDir)
  config.paths = config.frameworkConfig.next.paths
  config.database.migrationsDir = deriveMigrationsDir(namespace)

  const s = spinner()
  const coreDependencyPlan = getDependencyPlan(
    presetSelection.presets,
    presetSelection.integrations,
    project.linter.type === 'none'
  )
  const cliDependencyPlan = getCliDependencySyncPlan(cwd)
  s.start('Installing dependencies, This may take a moment')
  const depsResult = await installDependenciesAsync({
    cwd,
    pm,
    ...(isFreshProject && pm === 'pnpm'
      ? { pnpmAllowedBuilds: [...FRESH_PNPM_ALLOWED_BUILDS] }
      : {}),
    dependencies: Array.from(
      new Set([...coreDependencyPlan.dependencies, ...(cliDependencyPlan?.dependencies ?? [])])
    ),
    devDependencies: Array.from(
      new Set([
        ...coreDependencyPlan.devDependencies,
        ...(cliDependencyPlan?.devDependencies ?? [])
      ])
    )
  })
  if (depsResult.success) {
    s.stop('')
  } else {
    const message = redactSecrets(depsResult.error ?? 'Unknown dependency installation error')
    s.stop('Failed to install dependencies')
    p.log.warn(message)
    p.log.info(
      `You can install them manually:\n  ${pc.cyan(`${pm} add ${depsResult.dependencies.join(' ')}`)}\n  ${pc.cyan(`${pm} add -D ${depsResult.devDeps.join(' ')}`)}`
    )
    disposeCancelGuard()
    exitInit(
      'dependencies',
      message,
      message.includes('ERR_PNPM_UNEXPECTED_STORE')
        ? 'PNPM_UNEXPECTED_STORE'
        : 'DEPENDENCY_INSTALL_FAILED'
    )
  }

  s.start('Directory structure')
  scaffoldBase({
    cwd,
    config,
    includeEmailTemplatesDir: presetSelection.integrations.some(isEmailProviderIntegration)
  })

  s.message('TypeScript aliases')
  scaffoldTsconfig(cwd, config)

  s.message('Tailwind CSS')
  scaffoldTailwind(cwd, srcDir, namespace)

  s.message('Dev server')
  ensureDefaultDevScriptPort(cwd)

  s.message('Environment variables')
  scaffoldEnv(cwd, { databaseUrl, namespace })
  if (collectedIntegrationConfig.sections.length > 0) {
    appendEnvVars(
      cwd,
      collectedIntegrationConfig.sections,
      collectedIntegrationConfig.overwriteKeys
    )
  }

  s.message('Database')
  const dbFiles = scaffoldDatabase({ cwd, config })

  s.message('Authentication')
  scaffoldAuth({ cwd, config })

  s.message('Components')
  scaffoldComponents({ cwd, config })

  s.message('Pages & layouts')
  scaffoldLayout({ cwd, config })

  s.message('API routes')
  scaffoldApiRoutes({ cwd, config })

  s.message('Linter')
  if (project.linter.type === 'none') {
    scaffoldOxfmt(cwd, project.linter, config.paths.admin)
  }

  s.message('Next.js config')
  const nextConfigResult = scaffoldNextConfig({ cwd, nextMajorVersion })

  s.clear()
  if (nextConfigResult.status === 'unsupported') {
    p.log.warn('The Next.js config could not be updated automatically — review it manually.')
  }

  const drizzleConfigPath = path.join(cwd, 'drizzle.config.ts')
  if (!dbFiles.includes('drizzle.config.ts') && fs.existsSync(drizzleConfigPath)) {
    if (forceMode) {
      const { readNamespacedTemplate } =
        await import('@next-adapter/template-reader/read-namespaced-template.js')
      fs.writeFileSync(
        drizzleConfigPath,
        readNamespacedTemplate('drizzle.config.ts', namespace),
        'utf-8'
      )
      p.log.success('Updated drizzle.config.ts')
    } else if (!options.yes) {
      const overwrite = await p.confirm({
        message: 'drizzle.config.ts already exists. Overwrite with latest version?',
        initialValue: true
      })
      if (!p.isCancel(overwrite) && overwrite) {
        const { readNamespacedTemplate } =
          await import('@next-adapter/template-reader/read-namespaced-template.js')
        fs.writeFileSync(
          drizzleConfigPath,
          readNamespacedTemplate('drizzle.config.ts', namespace),
          'utf-8'
        )
        p.log.success('Updated drizzle.config.ts')
      }
    }
  }

  process.stdout.write('\x1B[2A\x1B[J')
  s.start('Generating core schemas')
  const coreSchemasResult = scaffoldCoreSchemas({ cwd, config })
  s.stop('')
  process.stdout.write('\x1B[2A\x1B[J')

  const presetInstallResult =
    presetSelection.presets.length > 0
      ? (() => {
          s.start(`Installing presets: ${presetSelection.presets.join(', ')}`)
          return installPresets({
            cwd,
            config,
            pm,
            presetIds: presetSelection.presets,
            interactive: !options.yes,
            includeLinter: project.linter.type === 'none',
            dependenciesInstalled: true
          })
        })()
      : Promise.resolve({ installed: [], skipped: [], warnings: [], config })
  const resolvedPresetInstallResult = await presetInstallResult
  if (presetSelection.presets.length > 0) {
    s.stop('')
    process.stdout.write('\x1B[2A\x1B[J')
  }

  const integrationInstallResult =
    presetSelection.integrations.length > 0
      ? (() => {
          s.start(`Installing integrations: ${presetSelection.integrations.join(', ')}`)
          return installIntegrations({
            cwd,
            config: resolvedPresetInstallResult.config,
            pm,
            integrationIds: presetSelection.integrations,
            interactive: false,
            includeLinter: project.linter.type === 'none',
            dependenciesInstalled: true
          })
        })()
      : Promise.resolve({
          installed: [],
          skipped: [],
          warnings: [],
          config: resolvedPresetInstallResult.config
        })
  const resolvedIntegrationInstallResult = await integrationInstallResult
  if (presetSelection.integrations.length > 0) {
    s.stop('')
    process.stdout.write('\x1B[2A\x1B[J')
  }

  writeConfigFile(cwd, resolvedIntegrationInstallResult.config)
  const usesLocalStorage = resolvedIntegrationInstallResult.config.storage.provider === 'local'

  for (const err of coreSchemasResult.errors) {
    p.log.warn(`Core schemas: ${err}`)
  }
  for (const warning of resolvedPresetInstallResult.warnings) {
    p.log.warn(warning)
  }
  for (const warning of resolvedIntegrationInstallResult.warnings) {
    p.log.warn(warning)
  }
  if (usesLocalStorage) {
    p.log.warn(
      'Local filesystem storage is only for development or self-hosted persistent-disk deployments. Use Vercel Blob, Railway Bucket, or Cloudflare R2 for hosted production.'
    )
  }

  let dbPushed = false
  if (depsResult.success && options.skipMigration && hasDbUrl(cwd)) {
    p.log.info(`Skipping database schema push ${pc.dim('(--skip-migration)')}`)
  } else if (depsResult.success && hasDbUrl(cwd)) {
    let driverReady = hasDrizzleKitPostgresDriverDependency(cwd)

    if (!driverReady) {
      s.start(
        `Installing Drizzle Kit Postgres driver dependency (${DRIZZLE_KIT_POSTGRES_DRIVER_DEP})`
      )
      const driverResult = await installDependenciesAsync({
        cwd,
        pm,
        dependencies: [],
        devDependencies: [DRIZZLE_KIT_POSTGRES_DRIVER_DEP]
      })

      if (!driverResult.success) {
        s.stop('Database push failed')
        p.log.warn(driverResult.error ?? `Failed to install ${DRIZZLE_KIT_POSTGRES_DRIVER_DEP}`)
        p.log.info(
          `Install ${DRIZZLE_KIT_POSTGRES_DRIVER_DEP} manually, then run: ${pc.cyan(pmExecCommand(pm, 'drizzle-kit push'))}`
        )
      } else {
        driverReady = hasDrizzleKitPostgresDriverDependency(cwd)
        s.stop(`Drizzle Kit Postgres driver dependency installed`)
      }
    }

    if (driverReady) {
      const useTerminalForDbPush = shouldUseTerminalForDbPush(options)
      let dbSpinnerVisible = true
      const clearDbSpinner = () => {
        if (!dbSpinnerVisible) return
        dbSpinnerVisible = false
        s.clear()
      }
      s.start(
        useTerminalForDbPush ? 'Setting up database' : 'Pushing database schema (drizzle-kit push)'
      )

      const pushResult = await runDrizzlePush(cwd, {
        interactive: useTerminalForDbPush,
        onOutput: clearDbSpinner
      })
      if (pushResult.success) {
        if (useTerminalForDbPush) {
          const verification = await verifyDatabaseReachable(cwd)
          clearDbSpinner()
          if (!verification.success) {
            p.log.warn(verification.error)
            const message = 'Database was not reachable. Aborting setup.'
            p.log.error(message)
            exitInit('database', verification.error, 'DATABASE_UNREACHABLE')
          }
        } else {
          clearDbSpinner()
        }
        dbPushed = true
      } else {
        if (dbSpinnerVisible) {
          dbSpinnerVisible = false
          s.stop('Database push failed')
        }
        const pushError = pushResult.error ?? 'Unknown error'
        p.log.warn(pushError)
        if (isDatabaseReachabilityError(pushError)) {
          const message = 'Database was not reachable. Aborting setup.'
          p.log.error(message)
          exitInit('database', pushError, 'DATABASE_UNREACHABLE')
        }
        p.log.info(`You can run it manually: ${pc.cyan(pmExecCommand(pm, 'drizzle-kit push'))}`)
      }
    }
  }

  let seedEmail: string | undefined
  let seedPassword: string | undefined
  let seedSuccess = false
  let adminAccountReady = false

  if (dbPushed && options.skipAdminCreation) {
    p.log.info(
      `Skipping admin user creation ${pc.dim(`(use ${pmExecCommand(pm, `betterstart seed`)} later)`)}`
    )
  }

  if (dbPushed && !options.yes && !options.skipAdminCreation) {
    const adminDir = config.paths?.admin ?? './admin'
    const authBasePath = `${resolveAdminNamespace(namespace).apiPath}/auth`
    let replaceExistingAdmin = false
    s.start('Setting up database')
    const adminCheck = await checkExistingAdmin(cwd, adminDir, authBasePath)
    s.clear()

    if (adminCheck.error) {
      p.log.warn(`Could not verify existing admin account ${pc.dim(`(${adminCheck.error})`)}`)
      if (isDatabaseReachabilityError(adminCheck.error)) {
        const message = 'Database was not reachable. Aborting setup.'
        p.log.error(message)
        exitInit('database', adminCheck.error, 'DATABASE_UNREACHABLE')
      }
    } else if (adminCheck.existingAdmin) {
      const existingAdminLabel = formatAdminIdentity(adminCheck.existingAdmin)
      const adminAction = await p.select({
        message: 'Found an already existing admin account. Do you want to replace it or skip?',
        options: [
          {
            value: 'replace' as const,
            label: 'Replace',
            hint: existingAdminLabel
          },
          {
            value: 'skip' as const,
            label: 'Skip'
          }
        ]
      })

      if (p.isCancel(adminAction)) {
        p.cancel('Setup cancelled.')
        process.exit(0)
      }

      if (adminAction === 'skip') {
        adminAccountReady = true
        p.log.info(`Keeping existing admin account ${pc.dim(`(${existingAdminLabel})`)}`)
      } else {
        replaceExistingAdmin = true
      }
    }

    if (!adminAccountReady) {
      const credentials = await p.group(
        {
          email: () =>
            p.text({
              message: 'Admin email',
              placeholder: 'admin@example.com',
              validate: (v) => {
                if (!v || !v.includes('@')) return 'Please enter a valid email'
              }
            }),
          password: () =>
            p.password({
              message: 'Admin password',
              validate: (v) => {
                if (!v || v.length < 8) return 'Password must be at least 8 characters'
              }
            })
        },
        {
          onCancel: () => {
            p.cancel('Setup cancelled.')
            process.exit(0)
          }
        }
      )

      seedEmail = credentials.email
      seedPassword = credentials.password
      const credentialPromptRows =
        clackPromptRows('Admin email', credentials.email) +
        clackPromptRows('Admin password', p.S_PASSWORD_MASK.repeat(credentials.password.length))
      let rowsBelowCredentialPrompts = 0

      let seedOverwriteMode: SeedOverwriteMode | undefined = replaceExistingAdmin
        ? 'admin'
        : undefined
      s.start(replaceExistingAdmin ? 'Replacing admin user' : 'Creating admin user')
      let seedResult = await runSeed(
        cwd,
        adminDir,
        authBasePath,
        credentials.email,
        credentials.password,
        seedOverwriteMode
      )

      if (seedResult.existingUser) {
        const existingAccountMessage = `${pc.yellow('▲')} An account already exists for ${credentials.email}`
        s.stop(existingAccountMessage)
        rowsBelowCredentialPrompts += clackLogRows(existingAccountMessage)
        const replaceAccountMessage = 'Replace the existing account with this email?'
        const replace = await p.confirm({
          message: replaceAccountMessage,
          initialValue: false
        })
        rowsBelowCredentialPrompts += clackPromptRows(
          replaceAccountMessage,
          replace === true ? 'Yes' : 'No'
        )
        if (!p.isCancel(replace) && replace) {
          seedOverwriteMode = 'email'
          s.start('Replacing admin user')
          seedResult = await runSeed(
            cwd,
            adminDir,
            authBasePath,
            credentials.email,
            credentials.password,
            seedOverwriteMode
          )
        }
      }

      if (seedResult.success) {
        const seedSuccessMessage =
          seedOverwriteMode === 'admin'
            ? `Admin user replaced`
            : `Admin user ${pc.green(seedEmail)} created`
        s.stop(seedSuccessMessage)
        rowsBelowCredentialPrompts += clackLogRows(seedSuccessMessage)
        eraseRowsAbove(credentialPromptRows, rowsBelowCredentialPrompts)
        seedSuccess = true
        adminAccountReady = true
      } else if (seedResult.error) {
        s.stop(`Failed to create admin user`)
        p.note(
          `${pc.red(seedResult.error)}\n\nRun manually: ${pc.cyan(pmExecCommand(pm, `betterstart seed`))}`,
          pc.red('Seed failed')
        )
        if (isDatabaseReachabilityError(seedResult.error)) {
          const message = 'Database was not reachable. Aborting setup.'
          p.log.error(message)
          exitInit('database', seedResult.error, 'DATABASE_UNREACHABLE')
        }
      }
    }
  }

  if (isFreshProject) {
    s.start('Creating initial git commit')
    try {
      execFileSync('git', ['init'], { cwd, stdio: 'pipe' })
      execFileSync('git', ['add', '.'], { cwd, stdio: 'pipe' })
      execFileSync('git', ['commit', '-m', 'Initial commit from BetterStart'], {
        cwd,
        stdio: 'pipe'
      })
      s.clear()
    } catch {
      s.stop('Git commit skipped')
    }
  }

  const adminNamespace = resolveAdminNamespace(namespace)
  const adminLoginUrl = `http://localhost:${detectDevPort(cwd)}${adminNamespace.routePath}/login`

  let deployProvider: DeployProvider
  if (options.deployProvider) {
    deployProvider = options.deployProvider
  } else if (options.yes) {
    deployProvider = 'none'
  } else {
    const initialDeployProvider: DeployProvider = railwaySessionPromise
      ? 'railway'
      : readLinkedProjectId(cwd)
        ? 'vercel'
        : 'none'
    deployProvider = await promptDeployProvider(initialDeployProvider)
  }

  let deployedUrl: string | undefined
  if (deployProvider === 'vercel') {
    const deployFlow = await runVercelDeployFlow({
      cwd,
      projectName,
      interactive: !options.yes,
      env: process.env
    })
    deployedUrl = deployFlow.url
    if (!deployFlow.ok) {
      if (options.yes) {
        const message = 'Vercel deploy did not complete.'
        p.log.error(message)
        exitInit('deployment', message, 'DEPLOYMENT_FAILED')
      }
      p.log.warn('Vercel deploy did not complete; continuing.')
    }
  } else if (deployProvider === 'railway') {
    try {
      const session = await getRailwaySession()
      const deployFlow = await runRailwayDeployFlow({
        session,
        cwd,
        service: options.railwayService,
        providerOverrides: buildRailwayProviderOverrides({
          databaseServiceName: railwayDatabaseServiceName,
          buildCommand: runCommand(detectPackageManager(cwd), 'build'),
          bucketResourceName: railwayBucketResourceName,
          bucketUrlStyle: railwayBucketUrlStyle
        })
      })
      deployedUrl = deployFlow.url
      if (!deployFlow.ok) {
        if (deployFlow.detail) p.log.message(pc.dim(redactSecrets(deployFlow.detail)))
        if (options.yes) {
          const message = 'Railway deploy did not complete.'
          p.log.error(message)
          exitInit('deployment', message, 'DEPLOYMENT_FAILED')
        }
        p.log.warn('Railway deploy did not complete; continuing.')
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      if (options.yes) {
        const failureMessage = `Railway deploy failed: ${message}`
        p.log.error(failureMessage)
        exitInit('deployment', failureMessage, 'DEPLOYMENT_FAILED')
      }
      p.log.warn(`Railway deploy failed: ${message}`)
    }
  }

  if (!options.yes && !options.skipDevServerStart) {
    const devCmd = runCommand(pm, 'dev')
    const startDev = await p.confirm({
      message: 'Start the development server?',
      initialValue: true
    })

    if (!p.isCancel(startDev) && startDev) {
      disposeCancelGuard()
      await startManagedDevServer(cwd, devCmd, adminLoginUrl, {
        email: seedSuccess && seedEmail ? seedEmail : undefined,
        password: seedSuccess && seedPassword ? seedPassword : undefined
      })
      return
    }
  }

  disposeCancelGuard()

  if (options.json) {
    writeInitJson(jsonContext, {
      success: true,
      namespace,
      configPath: CONFIG_FILE_NAME,
      databaseProvider,
      storageProvider: presetSelection.storage,
      deployProvider,
      presets: presetSelection.presets,
      integrations: presetSelection.integrations,
      adminUrl: adminLoginUrl,
      adminEmail: seedEmail ?? null,
      deployedUrl: deployedUrl ?? null
    })
    return
  }

  p.outro(`Admin ready at ${adminNamespace.routePath}`)
}
